import { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import moment from 'moment'
import Cookies from 'js-cookie'
import { toast } from 'react-toastify';
import { ActionMenu, BackFace, Badge, BlurOverlay, Breaker, Button, Div, Flexbox, FlipCardInner, FlipCardScene, FrontFace, InputField, MultiDropdown, ProgressBar, SearchBar, WrapperBox } from '../../Theme/GlobalStyles'
import { AngleLeft, AngleRight, ArrowRight, CloseCircle, Funnel, Location, Setting, User, Users } from '../../Theme/Icons'
import { globalTheme } from '../../Theme/Theme'
import Banner from "../../../assets/img/Dummyimg.png"
import { Span, Text } from '../../Theme/Typography'
import CustomCalendar from '../../Components/CustomCalendar'
import {
  classroomData,
  createClassroomRequest,
  getAvailableClassrooms,
  getMyClassroomRequests,
  clearClassroomSliceData,
  clearClassroomSliceStates,
  getApprovedClassroomRequests,
  addClassroomSelectedSchool,
  getRequestComments,
  addRequestComment,
  clearRequestComments,
  cancelClassroomRequest,
  updateClassroomRequest
} from '../../../redux/classroom/classroomSlice'
import {
  cancelABooking,
  clearCoworkSliceData,
  clearCoworkSliceStates,
  coworkData,
  getAllCoworkUsers,
  getMyBookings,
  postRoomBookings,
  postRoomBookingAdmin,
} from '../../../redux/cowork/coworkSlice'
import { checkFor30MinTimeBeforeBooking, convertToSlot, formatBookingTime, mergeSlots, SchoolIdsMap, SkeletonCard, slotRunsFor } from '../../../utils/util'
import AvailableRoomFilterSlide from '../../Components/SideFilter/AvailableRoomFilterSlide'
import DatePicker from '../../Components/DatePicker'
import CustomDropdown from '../../../Components/CustomDropdown/CustomDropdown'
import { MODULES } from '../../../redux/appModule/appModuleSlice'
import useInfiniteScroll from '../../../utils/CustomHooks/useInfiniteScroll'
import ModuleSwitcher from '../../Components/ModuleSwitcher'
import ConfirmLabBookingPopup from '../../Components/Popup/ConfirmLabBookingPopup'
import CreateBookingPopup from '../../Components/Popup/CreateBookingPopup'
import { canonicalSchoolId, SCHOOL_OPTIONS } from '../../Constants/schools'
import useLatestRequest from '../../../utils/CustomHooks/useLatestRequest'
import { isModuleAdmin } from '../../../Routes/AdminRoute'
import { bookingWindowEnd, bookingWindowMessage, isBeyondBookingWindow } from '../../Constants/bookingWindow'
import { toastOpts } from '../../../utils/toastConfig'

const MY_BOOKINGS_PAGE_SIZE = 12

const skeletonStyle = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
  borderRadius: '8px',
}

// Room image carousel styles — circular prev/next buttons over the image and
// pill-style dot indicators.
const carouselImg = { width: '100%', height: '100%', objectFit: 'cover' }
const carouselNavBtn = (side) => ({
  position: 'absolute',
  top: '50%',
  [side]: '10px',
  transform: 'translateY(-50%)',
  width: '30px',
  height: '30px',
  borderRadius: '50%',
  border: 'none',
  background: 'rgba(255,255,255,0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
  zIndex: 2,
})
const carouselDotsWrap = {
  position: 'absolute',
  bottom: '12px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '6px',
  zIndex: 2,
}
const carouselDot = (active) => ({
  width: active ? '18px' : '7px',
  height: '7px',
  borderRadius: '20px',
  background: active ? '#fff' : 'rgba(255,255,255,0.6)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
})

// Native, allocation-light helpers — no moment in the hot path.
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`)
// Booking times are stored naive-UTC and serialized with a Z suffix, and the
// whole app renders them via moment.utc(...). Extract HH:mm in UTC to match —
// local getHours() shifted every window by the timezone offset, which is why
// already-booked slots were not getting disabled.
const hhmmFromIso = (iso) => {
  const d = new Date(iso)
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`
}
// Minutes since midnight. Slot/booking overlap has to be compared numerically:
// as strings, "00:00" is the minimum, so a booking ending at midnight
// (22:30 -> 00:00) never satisfied `slotStart < window.end` and blocked nothing.
const minutesFromHHMM = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}
// A window whose end is at or before its start has run past midnight — clamp it
// to the end of the day (the grid only ever covers one date).
const toMinuteWindow = (startHHMM, endHHMM) => {
  const start = minutesFromHHMM(startHHMM)
  const end = minutesFromHHMM(endHHMM)
  return { start, end: end <= start ? 24 * 60 : end }
}
const formatHourLabel = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number)
  const period = h >= 12 ? 'PM' : 'AM'
  const hr12 = h % 12 || 12
  return `${hr12}:${pad2(m)} ${period}`
}

// Day keys as stored on rooms.availableDays (["mon","tue",...]), indexed by
// JS Date.getDay() (0 = Sunday).
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

// Normalize a SQL TIME ("HH:MM:SS" / "HH:MM") to "HH:MM"; null/blank -> null.
const timeToHHMM = (t) =>
  typeof t === 'string' && t.length >= 5 ? t.slice(0, 5) : null

// Build the day's bookable slots, constrained to a room's availability window
// and days. Defaults when the room has none configured: all 7 days, 7 AM to
// midnight (07:00–24:00). Returns [] when the selected date's weekday isn't available.
const generateTimeSlots = (duration, calendarDate, nowHHMM, availability = {}) => {
  const slots = []
  const todayStr = new Date().toISOString().slice(0, 10)
  const isToday = calendarDate === todayStr
  // Both are 'YYYY-MM-DD', so a string compare orders them correctly. Without
  // this every slot on a past date came back enabled — the time filter below
  // only ran for today, so yesterday's whole grid was bookable.
  const isPastDate = !!calendarDate && calendarDate < todayStr

  // Weekday gate — empty/missing availableDays means every day is available.
  const days =
    Array.isArray(availability.availableDays) && availability.availableDays.length
      ? availability.availableDays.map((d) => String(d).toLowerCase())
      : null
  if (days) {
    const dow = new Date(`${calendarDate}T00:00:00`).getDay()
    if (!days.includes(DAY_KEYS[dow])) return []
  }

  // Time window — default 7 AM to midnight when unset. Slots start at
  // availableFrom and never end past availableTo.
  const startHHMM = timeToHHMM(availability.availableFrom) || '07:00'
  const endHHMM = timeToHHMM(availability.availableTo) || '24:00'

  let [h, m] = startHHMM.split(':').map(Number)
  while (true) {
    const startStr = `${pad2(h)}:${pad2(m)}`
    if (startStr >= endHHMM) break
    let nm = m + duration
    let nh = h
    if (nm >= 60) {
      nh += Math.floor(nm / 60)
      nm = nm % 60
    }
    const endStr = `${pad2(nh)}:${pad2(nm)}`
    if (endStr > endHHMM) break
    slots.push({
      value: `${startStr} - ${endStr}`,
      label: formatHourLabel(startStr),
      disabled: isPastDate || (isToday && nowHHMM ? startStr < nowHHMM : false),
    })
    h = nh
    m = nm
  }
  return slots
}

// Resolve a participant's display name across the two attendee shapes:
// cowork bookings nest CoworkUser; classroom requests carry name/label.
const attendeeName = (a) =>
  `${a?.CoworkUser?.firstName || ''} ${a?.CoworkUser?.lastName || ''}`.trim() ||
  a?.name ||
  a?.label ||
  `${a?.firstName || ''} ${a?.lastName || ''}`.trim() ||
  'Guest'

// Equipment comes back as {id, name} on a request, but the room-card path maps
// plain strings — accept both.
const equipmentName = (e) => (typeof e === 'string' ? e : e?.name || 'Equipment')

// Booked-slot colour coding by the booker's role — mirrors the old Cowork
// "Available Rooms" timeline (Student / Staff / Faculty), extended with an Admin
// hue so admins can tell who holds each slot. Unknown/other roles fall back to a
// neutral grey. Colours match the old TimeSelector palette.
const BOOKING_ROLE_COLORS = {
  faculty: { bg: '#0052cc', text: '#ffffff', label: 'Faculty' },
  staff: { bg: '#9ca4af', text: '#1a1a1a', label: 'Staff' },
  admin: { bg: '#6c5ce7', text: '#ffffff', label: 'Admin' },
  student: { bg: '#bfe4fe', text: '#1a1a1a', label: 'Student' },
  other: { bg: '#e3e3e3', text: '#5a5a5a', label: 'Booked' },
}
// Legend order shown to elevated viewers.
const BOOKING_LEGEND = ['student', 'staff', 'faculty', 'admin']

// Pull role names off whatever user shape a booking carries.
const roleNamesOf = (user) => {
  const roles = user?.coworkRole || user?.roles || user?.role || []
  const arr = Array.isArray(roles) ? roles : [roles]
  return arr
    .map((r) => (typeof r === 'string' ? r : r?.roleName || r?.label || r?.name || ''))
    .filter(Boolean)
}
// Map a booker to a single colour bucket. Priority mirrors the old page
// (Faculty > Staff > Admin > Student), so a multi-role user resolves the same way.
const roleCategoryOf = (user) => {
  const names = roleNamesOf(user)
  const has = (kw) => names.some((n) => n.includes(kw))
  if (has('Faculty')) return 'faculty'
  if (has('Staff')) return 'staff'
  if (has('Admin')) return 'admin'
  if (has('Student') || has('User')) return 'student'
  return 'other'
}

// A room opts into shared-seat booking by carrying this tag in its `category`
// array — originally only true for Bloomberg's terminal room, now a per-room
// setting so any room can be flagged this way regardless of which module it's
// booked through.
const CAPACITY_BASED_CATEGORY = 'capacityBased'
const roomIsCapacityBased = (room) => {
  const cats = room?.category
  if (!cats) return false
  return (Array.isArray(cats) ? cats : [cats]).some((c) => String(c) === CAPACITY_BASED_CATEGORY)
}

// Isolated card — re-renders only when its own props/state change. Slot clicks
// stay local, so siblings don't re-render.
const RoomCard = memo(function RoomCard({
  room,
  index,
  flipped,
  calendarDate,
  participantOptions,
  bookingLoading,
  onFlip,
  onClose,
  onRequest,
  onDateChange,
  bmbRemainingMinutes = Infinity,
  singleSlot = false,
  showBookingRoles = false,
  maxBookingDate = null,
  maxBookingDateMessage,
}) {
  const [duration, setDuration] = useState('30')
  // Eduspace only: flags the request as tied to an event. The create-request
  // endpoint has always accepted `isEvent` — the student flow just never sent
  // it, so only the admin-side CreateBookingPopup could set it.
  const [isEvent, setIsEvent] = useState(false)
  const [title, setTitle] = useState('')
  const [participants, setParticipants] = useState([])
  const [selectedSlots, setSelectedSlots] = useState([])
  const [showCalendar, setShowCalendar] = useState(false)
  const schoolArray = SchoolIdsMap ? Object.keys(SchoolIdsMap) : []
  const [school, setSchool] = useState(
    "Masters Union"
  );
  const dispatch = useDispatch()
  const { selectedModule } = useSelector((state) => state.appModule)
  const isClassroom = room?.is_classroom

  // Room image carousel: use the room's image gallery, falling back to the
  // thumbnail, then a banner. Index wraps around so "next" past the last image
  // loops back to the first.
  const roomImages = useMemo(() => {
    const imgs = (room?.roomImages || []).filter(Boolean)
    if (imgs.length) return imgs
    if (room?.room_thumbnail) return [room.room_thumbnail]
    return [Banner]
  }, [room?.roomImages, room?.room_thumbnail])

  const [imgIndex, setImgIndex] = useState(0)
  const [isCardHovered, setIsCardHovered] = useState(false)
  // Reset to the first image if this card is reused for a different room.
  useEffect(() => { setImgIndex(0) }, [room?.id])

  const hasMultipleImages = roomImages.length > 1
  const goToPrevImage = useCallback((e) => {
    e?.stopPropagation()
    setImgIndex((i) => (i - 1 + roomImages.length) % roomImages.length)
  }, [roomImages.length])
  const goToNextImage = useCallback((e) => {
    e?.stopPropagation()
    setImgIndex((i) => (i + 1) % roomImages.length)
  }, [roomImages.length])

  const isCapacityBased = roomIsCapacityBased(room)
  const roomCapacity = useMemo(() => {
    const c = Number(room?.capacity)
    return Number.isFinite(c) && c > 0 ? Math.floor(c) : 11
  }, [room?.capacity])

  // Reset card-local state whenever this card is closed.
  useEffect(() => {
    if (!flipped) {
      setSelectedSlots([])
      setTitle('')
      setParticipants([])
      setShowCalendar(false)
      setIsEvent(false)
    }
  }, [flipped])

  // Booked windows: compute once per booking list. Also resolve the booker's
  // role bucket + name so each booked slot can be colour-coded (who booked it).
  // A slot is taken by EITHER a confirmed room_bookings row (status Booked) OR an
  // Approved request — the modules that approve requests don't always materialize
  // a booking row. Applies across all modules and regardless of who booked it.
  const bookedWindows = useMemo(() => {
    const windows = (room?.room_bookings || []).map((b) => {
      const user = b.CoworkUser || b.User || b.coworkuser || b.requestingUser || {}
      return {
        ...toMinuteWindow(hhmmFromIso(b.start_time), hhmmFromIso(b.end_time)),
        role: roleCategoryOf(user),
        bookedBy: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
      }
    })
    ;(room?.room_booking_requests || []).forEach((r) => {
      if (r?.status !== 'Approved') return
      const user = r.requestingUser || {}
      windows.push({
        ...toMinuteWindow(hhmmFromIso(r.start_time), hhmmFromIso(r.end_time)),
        role: roleCategoryOf(user),
        bookedBy: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
      })
    })
    return windows
  }, [room?.room_bookings, room?.room_booking_requests])

  // Current user id (from the login cookie) — used to block re-requesting a slot
  // the user already has a pending request for.
  const myId = useMemo(() => {
    try {
      const raw = Cookies.get('loginData')
      return raw ? JSON.parse(raw)?.id : null
    } catch (e) {
      return null
    }
  }, [])


  const seatCount = useMemo(() => {
    if (!isCapacityBased) return 1
    const invited = (participants || []).filter((p) => String(p?.cat) !== String(myId))
    return invited.length + 1
  }, [isCapacityBased, participants, myId])

  // Slots the CURRENT user already has an "Under Review" request for. Pending
  // requests don't block everyone (an admin may reject them), but the same user
  // shouldn't be able to request the same slot twice. Other users' pending
  // requests are intentionally excluded.
  const myPendingWindows = useMemo(() => {
    if (!myId) return []
    return (room?.room_booking_requests || [])
      .filter(
        (r) =>
          r?.status === 'Under Review' &&
          (r?.requestingUser?.id === myId || r?.user_id === myId)
      )
      .map((r) => toMinuteWindow(hhmmFromIso(r.start_time), hhmmFromIso(r.end_time)))
  }, [room?.room_booking_requests, myId])

  const isMyPendingSlot = useCallback(
    (slotValue) => {
      if (!myPendingWindows.length) return false
      const sep = slotValue.indexOf(' - ')
      const s = minutesFromHHMM(slotValue.slice(0, sep))
      const e = minutesFromHHMM(slotValue.slice(sep + 3))
      return myPendingWindows.some((w) => s < w.end && e > w.start)
    },
    [myPendingWindows]
  )

  // Recompute "now" only when calendarDate flips to/from today.
  const nowHHMM = useMemo(() => {
    const d = new Date()
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
  }, [calendarDate])

  // Room availability window/days drive which slots exist (memoized by identity
  // so the slot memos below only recompute when they actually change).
  const roomAvailability = useMemo(
    () => ({
      availableFrom: room?.availableFrom,
      availableTo: room?.availableTo,
      availableDays: room?.availableDays,
    }),
    [room?.availableFrom, room?.availableTo, room?.availableDays]
  )

  // Used by the front face for availability + the back face when duration=30.
  const slots30 = useMemo(
    () => generateTimeSlots(30, calendarDate, nowHHMM, roomAvailability),
    [calendarDate, nowHHMM, roomAvailability]
  )

  const slotsForDuration = useMemo(() => {
    if (duration === '30') return slots30
    return generateTimeSlots(Number(duration) || 30, calendarDate, nowHHMM, roomAvailability)
  }, [duration, slots30, calendarDate, nowHHMM, roomAvailability])

  // Bloomberg lab books in longer blocks (30 / 60); every other module keeps
  // the original 30 / 15 pair.
  const durationOptions = useMemo(
    () => (selectedModule === MODULES.BLOOMBERG ? ['30', '60'] : ['30', '15']),
    [selectedModule]
  )

  // If the module changes while a card is open, a duration from the previous
  // module (e.g. 15 on Bloomberg) would leave the toggle with nothing selected
  // and generate slots the module doesn't offer. Fall back to 30.
  useEffect(() => {
    if (!durationOptions.includes(duration)) setDuration('30')
  }, [durationOptions, duration])

  // Returns the booking window covering this slot (with role + booker), or null.
  const bookingForSlot = useCallback(
    (slotValue) => {
      if (!bookedWindows.length) return null
      const sep = slotValue.indexOf(' - ')
      const s = minutesFromHHMM(slotValue.slice(0, sep))
      const e = minutesFromHHMM(slotValue.slice(sep + 3))
      for (let i = 0; i < bookedWindows.length; i++) {
        const w = bookedWindows[i]
        // Any overlap blocks the slot — containment alone missed slots that only
        // partially intersect a booking (e.g. misaligned availability offsets).
        if (s < w.end && e > w.start) return w
      }
      return null
    },
    [bookedWindows]
  )


  const bmbSeatWindows = useMemo(() => {
    if (!isCapacityBased) return []
    return (room?.room_bookings || []).map((b) => ({
      ...toMinuteWindow(hhmmFromIso(b.start_time), hhmmFromIso(b.end_time)),
      seats: Math.max(1, Number(b?.participants) || 1),
    }))
  }, [room?.room_bookings, isCapacityBased])


  const seatsLeftForSlot = useCallback(
    (slotValue) => {
      if (!isCapacityBased) return null
      const sep = slotValue.indexOf(' - ')
      const start = minutesFromHHMM(slotValue.slice(0, sep))
      const endRaw = minutesFromHHMM(slotValue.slice(sep + 3))
      const end = endRaw <= start ? 24 * 60 : endRaw

      let left = roomCapacity
      for (let m = start; m < end; m += 30) {
        const used = bmbSeatWindows.reduce(
          (sum, w) => (m < w.end && m + 30 > w.start ? sum + w.seats : sum),
          0
        )
        left = Math.min(left, roomCapacity - used)
      }
      return Math.max(0, left)
    },
    [isCapacityBased, bmbSeatWindows, roomCapacity]
  )

  const stats = useMemo(() => {
    const totalSlots = slots30.length
    let activeCount = 0
    let bookedCount = 0
    for (let i = 0; i < slots30.length; i++) {
      const s = slots30[i]
      if (s.disabled) continue
      activeCount++
      // Capacity-based rooms are booked per seat, so a slot only counts as
      // gone once every seat in it is taken — not the moment someone books it.
      if (isCapacityBased) {
        if (seatsLeftForSlot(s.value) <= 0) bookedCount++
      } else if (bookedWindows.length && bookingForSlot(s.value)) {
        bookedCount++
      }
    }
    const availableCount = activeCount - bookedCount
    const availablePercent = totalSlots > 0 ? Math.round((availableCount / totalSlots) * 100) : 0
    return { totalSlots, availableCount, availablePercent }
  }, [slots30, bookedWindows, bookingForSlot, isCapacityBased, seatsLeftForSlot])

  const equipmentDisplay = useMemo(() => {
    const names = (room?.equipments || []).map((e) => e.name || e)
    if (names.length === 0) return ''
    return names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ')
  }, [room?.equipments])

  // Amenities come back from fetchRoomsAndSlots as [{ id, name }] under
  // `amenities`. The card used to print a hardcoded "TV, Whiteboard" here and
  // never touched the real list, so a room's actual amenities never showed.
  const amenityDisplay = useMemo(() => {
    const names = (room?.amenities || []).map((a) => a?.name || a).filter(Boolean)
    if (names.length === 0) return ''
    return names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', ')
  }, [room?.amenities])

  // Full list for the hover title, so a truncated display is still readable.
  const amenityFullList = useMemo(
    () => (room?.amenities || []).map((a) => a?.name || a).filter(Boolean).join(', '),
    [room?.amenities]
  )

  // Minutes each slot chip represents (the 30/15 Mins toggle).
  const slotMinutes = Number(duration) || 30

  const toggleSlot = useCallback((slotValue) => {
    setSelectedSlots((prev) => {
      const alreadySelected = prev.includes(slotValue)
      // Single-slot roles (Cowork Student/Staff/Faculty) may hold only one slot at
      // a time — picking a new one replaces the previous selection, clicking the
      // selected one clears it.
      const next = singleSlot
        ? (alreadySelected ? [] : [slotValue])
        : alreadySelected
          ? prev.filter((s) => s !== slotValue)
          : [...prev, slotValue]
      // Bloomberg: the user's remaining allowance for this DAY (1 hour minus what
      // they've already booked/requested in any room). Infinity elsewhere, so this
      // never fires outside Bloomberg. Deselecting is always allowed — only block
      // additions that would push past what's left.
      if (next.length > prev.length && next.length * slotMinutes > bmbRemainingMinutes) {
        toast.error(
          bmbRemainingMinutes <= 0
            ? 'You have already used your 1 hour for this day.'
            : `Bloomberg users can book up to 1 hour per day — you have ${bmbRemainingMinutes} minute(s) left.`,
          toastOpts('bmb-daily-limit')
        )
        return prev
      }
      return next
    })
  }, [bmbRemainingMinutes, slotMinutes, singleSlot])

  // Raising the party size can invalidate slots already picked (they had room
  // for 2, not for 5). Drop those instead of letting the request fail server-side.
  useEffect(() => {
    if (!isCapacityBased) return
    setSelectedSlots((prev) => {
      const stillFits = prev.filter((s) => seatsLeftForSlot(s) >= seatCount)
      return stillFits.length === prev.length ? prev : stillFits
    })
  }, [seatCount, isCapacityBased, seatsLeftForSlot])

  const handleRequest = () => {
    onRequest(room, {
      slots: selectedSlots,
      title,
      participants,
      duration,
      // Only Eduspace renders the toggle; force false elsewhere so a stale
      // value can never ride along on another module's request.
      isEvent: selectedModule === MODULES.EDUSPACE ? isEvent : false,
      // Capacity-based rooms book per seat; every other room takes the whole room.
      seatCount: isCapacityBased ? seatCount : 1,
    })
  }

  const handleSelectOption = (option) => {
      switch (option.type) {
        case "school":
          setSchool(option.value);
          dispatch(addClassroomSelectedSchool(option.value));
          break;
  
        default:
          break;
      }
    };

  return (
    <FlipCardScene active={flipped}>
      <FlipCardInner flipped={flipped}>
        {/* Front Side */}
        <FrontFace
          onMouseEnter={() => setIsCardHovered(true)}
          onMouseLeave={() => setIsCardHovered(false)}
        >
          <WrapperBox
            borderRadius={50}
            padding="14px 14px 20px"
            borderColor={globalTheme.colors.nuetral2}
            className="respCardPad"
            style={{ height: '100%' }}
          >
            {stats.availableCount > 0 && (
              <Badge
                green
                statusText={'Available now'}
                medium
                className={'absoluteBadge'}
                borderRadius={'80px'}
              />
            )}

            <Div width="100%" Height="300px" borderRadius="20" overflow="hidden">
              <img
                src={roomImages[imgIndex] || Banner}
                alt={`${room.room_name || 'room'} ${imgIndex + 1}`}
                style={carouselImg}
                onError={(e) => { e.currentTarget.src = Banner }}
              />

              {hasMultipleImages && (
                <>
                  {/* Arrows reveal on card hover; dots stay visible. */}
                  {isCardHovered && (
                    <>
                      <button type="button" aria-label="Previous image" style={carouselNavBtn('left')} onClick={goToPrevImage}>
                        <AngleLeft />
                      </button>
                      <button type="button" aria-label="Next image" style={carouselNavBtn('right')} onClick={goToNextImage}>
                        <AngleRight />
                      </button>
                    </>
                  )}
                  <div style={carouselDotsWrap}>
                    {roomImages.map((_, i) => (
                      <span
                        key={i}
                        style={carouselDot(i === imgIndex)}
                        onClick={(e) => { e.stopPropagation(); setImgIndex(i) }}
                      />
                    ))}
                  </div>
                </>
              )}
            </Div>

            <Text font16 inter medium black3 className={'mt20'}>
              {room.room_name}
            </Text>

            <Flexbox className="mt16 mb16" gap={8} alignCenter>
              <Location />
              <Text font14 inter regular grey2>
                Tower {room.tower}
              </Text>
            </Flexbox>

            <Flexbox className="mb30 respMetaRow" gap={20} alignCenter>
              <Flexbox gap={8} alignCenter maxWidth="max-content">
                <Users />
                <Text font14 inter regular grey2>{room.capacity} Seats</Text>
              </Flexbox>

              {/* The room's real amenities. This used to be the literal string
                  "TV, Whiteboard" for every room, regardless of what it had. */}
              {amenityDisplay && (
                <Flexbox gap={8} alignCenter maxWidth="max-content" title={amenityFullList}>
                  <Setting />
                  <Text font14 inter regular grey2>
                    {amenityDisplay}
                  </Text>
                </Flexbox>
              )}
            </Flexbox>

            <ProgressBar
              percentage
              title={`Today's availability (${stats.availableCount}/${stats.totalSlots})`}
              width={stats.availablePercent}
            />

            <Button
              name="View & Book Slots"
              icon={<ArrowRight />}
              buttonMd
              primaryOutlined
              fullBtn
              className="mt30"
              onClick={() => onFlip(index)}
            />
          </WrapperBox>
        </FrontFace>

        {/* Back Side — only mounted while flipped to skip its render work otherwise */}
        {flipped && (
          <BackFace>
            {/* Flex column that fills the card height: a non-scrolling top
                section (header + form) keeps the dropdowns/calendar from being
                clipped, the slots grid is the single scroll region, and the
                summary + action buttons stay pinned at the bottom.
                NOTE: uses Div (not WrapperBox) because WrapperBox drops `style`,
                so the flex layout would silently not apply. */}
            <Div
              Height={'100%'}
              borderRadius={50}
              padding={'20px'}
              overflow={'visible'}
              style={{
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                border: `1px solid ${globalTheme.colors.nuetral2}`,
                background: globalTheme.colors.white1,
                boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Flexbox spaceBW alignStart>
                <Text font18 inter semiBold black3>
                  {room.room_name}
                </Text>
                <CloseCircle onClick={onClose} className="pointer" />
              </Flexbox>

              <Flexbox className="mt12" gap={8} alignCenter wrap>
                <Flexbox gap={6} alignCenter maxWidth="max-content">
                  <Location />
                  <Text font12 inter regular grey2>
                    Tower {room.tower}
                  </Text>
                </Flexbox>
                <Text font12 inter regular nuetral6>·</Text>
                <Flexbox gap={6} alignCenter maxWidth="max-content">
                  <Users />
                  <Text font12 inter regular grey2>{room.capacity} Seats</Text>
                </Flexbox>
                {equipmentDisplay && (
                  <>
                    <Text font12 inter regular nuetral6>·</Text>
                    <Flexbox gap={6} alignCenter maxWidth="max-content">
                      <Setting />
                      <Text font12 inter regular grey2>{equipmentDisplay}</Text>
                    </Flexbox>
                  </>
                )}
                {amenityDisplay && (
                  <>
                    <Text font12 inter regular nuetral6>·</Text>
                    <Flexbox gap={6} alignCenter maxWidth="max-content" title={amenityFullList}>
                      <Setting />
                      <Text font12 inter regular grey2>{amenityDisplay}</Text>
                    </Flexbox>
                  </>
                )}
              </Flexbox>

              <Breaker marginTop={16} marginBottom={16} />

              <Text font11 inter semiBold nuetral6 uppercase className="mb12" style={{ letterSpacing: 0.6 }}>
                Slot Duration
              </Text>

              <WrapperBox borderRadius={80} borderColor={globalTheme.colors.nuetral2} background={globalTheme.colors.nuetral2} padding={'4px 6px'}>
                <Flexbox gap={8}>
                  {durationOptions.map((option) => (
                    <Button
                      key={option}
                      buttonMd {...(duration === option ? { whiteFilled: true } : '')} fullBtn
                      name={`${option} Mins.`}
                      onClick={() => {
                        // Slot values are duration-specific ("09:00 - 09:30"),
                        // so a selection made at one length is meaningless at
                        // another — clear it rather than submit a stale slot.
                        if (option !== duration) setSelectedSlots([])
                        setDuration(option)
                      }}
                    />
                  ))}
                </Flexbox>
              </WrapperBox>

              <InputField
                noLabel
                placeholder={selectedModule === MODULES.EDUSPACE ? "Purpose of booking" : "Enter meeting title"}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt12"
                onKeyDown={() => {}}
              />

              <Flexbox gap={12} className="mt12 respFieldRow" alignStart>
                {/* Relative wrapper so the calendar popup anchors directly under
                    the date button instead of floating to the card corner. */}
                <div style={{ position: 'relative', flex: 1 }}>
                  <Button
                    primaryOutlined
                    buttonMd
                    fullBtn
                    name={moment(calendarDate).format('MMM DD-YYYY')}
                    onClick={() => setShowCalendar((s) => !s)}
                  />
                  {showCalendar && (
                    <CustomCalendar
                      align="left"
                      selectedDate={calendarDate}
                      // A booking can't be made in the past — the backend rejects
                      // it anyway, so don't let the date be picked to begin with.
                      disablePast
                      // ...and not past the booking window at the other end.
                      maxDate={maxBookingDate}
                      {...(maxBookingDateMessage ? { maxDateMessage: maxBookingDateMessage } : {})}
                      onSelectDate={(dateStr) => { onDateChange?.(dateStr); setShowCalendar(false) }}
                      onClose={() => setShowCalendar(false)}
                    />
                  )}
                </div>
                <MultiDropdown
                  noLabel
                  maxWidth={'195px'}
                  placeholder="Add Participants"
                  options={participantOptions}
                  selectedValues={participants}
                  onChange={setParticipants}
                />
              </Flexbox>

              
              {isCapacityBased && (
                <Flexbox gap={6} alignCenter className="mt12">
                  <Text font12 inter semiBold black3 maxWidth="max-content">
                    Booking {seatCount} of {roomCapacity} seats
                  </Text>
                  <Text font12 inter regular nuetral6 maxWidth="max-content">
                    {seatCount > roomCapacity
                      ? `— that's more people than the room holds, remove ${seatCount - roomCapacity}`
                      : seatCount === 1
                        ? '— just you. Add participants to book more seats.'
                        : `— you + ${seatCount - 1} participant${seatCount - 1 === 1 ? '' : 's'}`}
                  </Text>
                </Flexbox>
              )}

              {/* Eduspace only. School picker and the "Is this booking related to
                  an event?" toggle (the isEvent flag on the request) share one
                  row — the same pairing as the admin-side CreateBookingPopup —
                  so the slot list below keeps a row of vertical space. On a
                  narrow phone respFieldRow stacks them again. */}
              {selectedModule === MODULES.EDUSPACE && (
                <Flexbox gap={12} alignCenter className="mt12 respFieldRow">
                  <Div overflow="visible" style={{ flex: 1, minWidth: 0 }}>
                    <CustomDropdown
                      className="fullWidthDrop"
                      isimp={false}
                      dropName={"School"}
                      options={[
                        { name: "TETR", type: "school", value: "TETR" },
                        { name: "UG", type: "school", value: "UG" },
                        { name: "PGP", type: "school", value: "PGP" },
                        { name: "EXECUTIVE EDUCATION", type: "school", value: "Exec Education" },
                        { name: "CENTRAL FUNCTIONS", type: "school", value: "Central Functions" },
                      ]}
                      onSelect={handleSelectOption}
                    />
                  </Div>
                  <Flexbox spaceBW alignCenter gap={10} maxWidth="max-content" style={{ flexShrink: 0 }}>
                    <Text font12 inter medium black4 maxWidth="max-content">
                      Event booking?
                    </Text>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isEvent}
                      aria-label="Is this booking related to an event?"
                      onClick={() => setIsEvent((v) => !v)}
                      style={{
                        width: 46,
                        height: 26,
                        flexShrink: 0,
                        borderRadius: 999,
                        border: 'none',
                        cursor: 'pointer',
                        padding: 3,
                        display: 'flex',
                        justifyContent: isEvent ? 'flex-end' : 'flex-start',
                        alignItems: 'center',
                        background: isEvent ? globalTheme.colors.primary : globalTheme.colors.nuetral2,
                        transition: 'background 0.15s ease',
                      }}
                    >
                      <span
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: globalTheme.colors.white1,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                        }}
                      />
                    </button>
                  </Flexbox>
                </Flexbox>
              )}

              <Text font11 inter semiBold nuetral6 uppercase className="mt16 mb8" style={{ letterSpacing: 0.6 }}>
                Available Slots
              </Text>

              {/* Legend — only for elevated viewers (admin/staff/faculty), so they
                  can read which role holds each booked slot. Students just see
                  slots as booked with no "who". */}
              {showBookingRoles && (
                <Flexbox gap={12} wrap className="mb8" maxWidth="100%">
                  {BOOKING_LEGEND.map((key) => {
                    const c = BOOKING_ROLE_COLORS[key]
                    return (
                      <Flexbox key={key} gap={6} alignCenter maxWidth="max-content">
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: c.bg,
                            border: '1px solid rgba(0,0,0,0.12)',
                            flexShrink: 0,
                          }}
                        />
                        <Text font11 inter regular grey2>{c.label}</Text>
                      </Flexbox>
                    )
                  })}
                </Flexbox>
              )}

              {/* Bloomberg only — say why the slots are greyed out instead of
                  leaving the user to guess. */}
              {bmbRemainingMinutes !== Infinity && bmbRemainingMinutes <= 0 && (
                <Text font12 inter medium className="mb8" style={{ color: globalTheme.colors.red }}>
                  You have used your 1 hour for this day. Pick another date to book more.
                </Text>
              )}

              {/* Single scroll region — flexes to fill the remaining height so the
                  footer below it never gets pushed off the card. minHeight 0 lets
                  it shrink fully when the summary appears, so nothing spills.
                  Wrapped as a subtle panel so the slots read as one section. */}
              <Flexbox
                wrap
                gap={8}
                className="mb12"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  alignContent: 'flex-start',
                  background: globalTheme.colors.grey8,
                  border: `1px solid ${globalTheme.colors.nuetral2}`,
                  borderRadius: 16,
                  padding: 12,
                }}
              >
                {slotsForDuration.length === 0 && (
                  <Flexbox justiCenter alignCenter style={{ width: '100%', padding: '24px 0' }}>
                    <Text font13 inter medium nuetral6>
                      This room isn’t available on the selected date.
                    </Text>
                  </Flexbox>
                )}
                {slotsForDuration.map((slot, slotIndex) => {
                  const selected = selectedSlots.includes(slot.value)
                  const seatsLeft = seatsLeftForSlot(slot.value)
                  const booking = bookingForSlot(slot.value)
                  
                  const booked = isCapacityBased ? seatsLeft <= 0 : !!booking

                  // Booked slots render as a colour-coded, non-interactive chip.
                  // Elevated viewers see the booker's role colour (+ name on hover);
                  // everyone else sees a neutral "Booked" chip — no "who" revealed.
                  if (booked) {
                    
                    const c = showBookingRoles && booking
                      ? BOOKING_ROLE_COLORS[booking.role] || BOOKING_ROLE_COLORS.other
                      : BOOKING_ROLE_COLORS.other
                    const tip = isCapacityBased
                      ? `All ${roomCapacity} seats booked`
                      : showBookingRoles && booking
                        ? booking.bookedBy
                          ? `${c.label} · ${booking.bookedBy}`
                          : c.label
                        : 'Booked'
                    return (
                      <div
                        key={slotIndex}
                        title={tip}
                        style={{
                          padding: '8px 14px',
                          borderRadius: 50,
                          fontSize: 13,
                          fontWeight: 500,
                          fontFamily: 'inherit',
                          background: c.bg,
                          color: c.text,
                          border: `1px solid ${c.bg}`,
                          whiteSpace: 'nowrap',
                          cursor: 'not-allowed',
                          userSelect: 'none',
                        }}
                      >
                        {slot.label}
                      </div>
                    )
                  }

                  // The current user already has a pending request for this slot —
                  // show it as a non-interactive "Requested" chip so they can't
                  // request it again while it's awaiting approval.
                  if (isMyPendingSlot(slot.value)) {
                    return (
                      <div
                        key={slotIndex}
                        title="You've already requested this slot — awaiting approval"
                        style={{
                          padding: '8px 14px',
                          borderRadius: 50,
                          fontSize: 13,
                          fontWeight: 500,
                          fontFamily: 'inherit',
                          background: globalTheme.colors.yellow2,
                          color: globalTheme.colors.warning4,
                          border: `1px solid ${globalTheme.colors.warning2}`,
                          whiteSpace: 'nowrap',
                          cursor: 'not-allowed',
                          userSelect: 'none',
                        }}
                      >
                        {slot.label}
                      </div>
                    )
                  }

                  // Bloomberg: grey out any slot that would exceed the user's
                  // remaining hour for this day. Once the hour is fully used
                  // (remaining = 0) every unselected slot is disabled, in every
                  // room. Already-selected slots stay clickable so they can be
                  // deselected. Non-Bloomberg → remaining is Infinity → no effect.
                  const exceedsBmbAllowance =
                    !selected && (selectedSlots.length + 1) * slotMinutes > bmbRemainingMinutes
                  const seatsTooFew = isCapacityBased && !selected && seatsLeft < seatCount
                  const disabled = slot.disabled || exceedsBmbAllowance || seatsTooFew
                  return (
                    <Button
                      name={
                        isCapacityBased && seatsLeft < roomCapacity
                          ? `${slot.label} · ${seatsLeft} left`
                          : slot.label
                      }
                      key={slotIndex}
                      title={
                        seatsTooFew
                          ? `Only ${seatsLeft} of ${roomCapacity} seat(s) left — you're booking ${seatCount}`
                          : undefined
                      }
                      buttonSm
                      {...(selected ? { primaryFilled: true } : { primaryOutlined: true })}
                      disabled={disabled}
                      onClick={() => toggleSlot(slot.value)}
                    />
                  )
                })}
              </Flexbox>

              {/* Pinned footer */}
              <Div style={{ flexShrink: 0 }}>
                {/* {selectedSlots.length > 0 && (
                  <WrapperBox className="mb12" padding={'14px'} borderRadius={50} borderColor={globalTheme.colors.nuetral2} background={globalTheme.colors.grey8}>
                    <Flexbox alignCenter gap={24}>
                      <Text font16 inter semiBold black3>
                        {moment(calendarDate).format('MMM DD')} — {selectedSlots.length} slot(s) selected
                      </Text>
                    </Flexbox>
                  </WrapperBox>
                )} */}

                <Flexbox gap={12} alignCenter>
                  <Button buttonLg fullBtn primaryOutlined name="Cancel" onClick={onClose} />
                  <Button
                    buttonLg
                    fullBtn
                    primaryFilled
                    name={bookingLoading ? 'Requesting...' : 'Request Slot'}
                    primary
                    onClick={handleRequest}
                    disabled={bookingLoading}
                  />
                </Flexbox>
              </Div>
            </Div>
          </BackFace>
        )}
      </FlipCardInner>
    </FlipCardScene>
  )
})

// Small inline speech-bubble icon for the "View Comments" affordance — no
// dedicated comment glyph exists in the shared icon set.
const CommentIcon = ({ color = globalTheme.colors.primary }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const initialsFor = (firstName = '', lastName = '') => {
  const a = (firstName || '').trim()[0] || ''
  const b = (lastName || '').trim()[0] || ''
  return (a + b).toUpperCase() || '—'
}

// A row is EITHER a user comment OR a system `activity` message. The two get
// deliberately different weight: a comment is something a person said, so it
// carries their avatar and name; an activity is something that happened to the
// booking, so it reads as a quiet timeline line.
const CommentRow = ({ comment }) => {
  const isActivity =
    comment?.type === 'activity' || (!comment?.comment && !!comment?.activity)
  const c = comment?.commenter || {}
  // The actual moment, not "2 days ago" — the timeline is a record of what
  // happened to the booking, so the date is the useful part.
  const when = comment?.created_at
    ? moment(comment.created_at).format('DD MMM YYYY, hh:mm A')
    : ''

  // Activity: no avatar chrome and no "Activity" heading — the sentence already
  // says who did what ("Devansh Chauhan approved this booking request"), so a
  // label above it was pure duplication. A small dot marks the timeline instead,
  // in a 32px column so it lines up with the comment avatars below it.
  if (isActivity) {
    return (
      <Flexbox gap={10} alignStart noWrap>
        <Flexbox
          justiCenter
          style={{ width: 32, minWidth: 32, flexShrink: 0, paddingTop: 6 }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              minWidth: 6,
              borderRadius: '50%',
              background: globalTheme.colors.nuetral6,
              display: 'block',
            }}
          />
        </Flexbox>
        <Text
          font12
          inter
          regular
          nuetral6
          style={{ flex: 1, minWidth: 0, wordBreak: 'break-word', fontStyle: 'italic' }}
        >
          {comment?.activity}
          <span style={{ fontStyle: 'normal', opacity: 0.7, whiteSpace: 'nowrap' }}>
            {' · '}{when}
          </span>
        </Text>
      </Flexbox>
    )
  }

  return (
    <Flexbox gap={10} alignStart noWrap>
      {/* width + minWidth + flexShrink all pinned: Flexbox defaults to
          width:100%, and a bare `maxWidth="max-content"` used to clamp the box
          down to the width of the initials, squashing the circle into an oval. */}
      <Flexbox
        alignCenter
        justiCenter
        background={globalTheme.colors.nuetral2}
        style={{
          width: 32,
          height: 32,
          minWidth: 32,
          flexShrink: 0,
          borderRadius: '50%',
        }}
      >
        <Text font11 inter semiBold black3 style={{ lineHeight: 1 }}>
          {initialsFor(c.firstName, c.lastName)}
        </Text>
      </Flexbox>
      <Div overflow="visible" style={{ flex: 1, minWidth: 0 }}>
        <Flexbox gap={8} alignCenter>
          <Text font13 inter semiBold black3>
            {`${c.firstName || ''} ${c.lastName || ''}`.trim() || 'User'}
          </Text>
          <Text font11 inter regular nuetral6>{when}</Text>
        </Flexbox>
        <Text
          font13
          inter
          regular
          grey2
          className="mt4"
          style={{ wordBreak: 'break-word' }}
        >
          {comment?.comment}
        </Text>
      </Div>
    </Flexbox>
  )
}

// Loading placeholder for a My Bookings card. Shaped like the real card below
// (room name, title, tower line, slot pill, action) rather than reusing the room
// SkeletonCard, whose 160px image block sits at the top of a card that has no
// image — the layout would visibly jump when the data landed. Same shimmer and
// card chrome, so the two read as one system.
const BookingSkeletonCard = () => (
  <WrapperBox
    borderRadius={30}
    padding="30px 20px"
    borderColor={globalTheme.colors.nuetral2}
    maxWidth={'32%'}
    className="respCard3"
    style={{ minHeight: 360, boxSizing: 'border-box' }}
  >
    <Flexbox spaceBW alignCenter gap={10} noWrap>
      <div style={{ ...skeletonStyle, width: '55%', height: '20px' }} />
      <div style={{ ...skeletonStyle, width: '46px', height: '14px', flexShrink: 0 }} />
    </Flexbox>
    <div style={{ ...skeletonStyle, width: '75%', height: '14px', marginTop: '10px' }} />
    <Flexbox className="mt16 mb20" gap={8} alignCenter>
      <div style={{ ...skeletonStyle, width: '18px', height: '18px', borderRadius: '50%' }} />
      <div style={{ ...skeletonStyle, width: '110px', height: '14px' }} />
    </Flexbox>
    {/* The slot pill */}
    <div style={{ ...skeletonStyle, width: '100%', height: '56px', borderRadius: '50px' }} />
    <div style={{ ...skeletonStyle, width: '40%', height: '14px', marginTop: '24px' }} />
    <Flexbox wrap gap={8} className="mt12">
      <div style={{ ...skeletonStyle, width: '84px', height: '22px', borderRadius: '20px' }} />
      <div style={{ ...skeletonStyle, width: '64px', height: '22px', borderRadius: '20px' }} />
    </Flexbox>
    <div style={{ ...skeletonStyle, width: '100%', height: '44px', marginTop: '30px', borderRadius: '50px' }} />
  </WrapperBox>
)

// Booking-request card with a flip-to-comments back face. Comments live only on
// Makers Lab / Eduspace requests (not Cowork bookings), so this card is used
// exclusively in the non-Cowork branch of the My Bookings tab. Mirrors the
// RoomCard flip pattern: clicking "View Comments" rotates the card.
const RequestCard = memo(function RequestCard({
  request,
  flipped,
  comments,
  isCommentsFetching,
  isPostingComment,
  commentPostSuccess,
  onFlip,
  onClose,
  onCancel,
  onEdit,
  onPostComment,
}) {
  const [commentText, setCommentText] = useState('')
  const { selectedModule } = useSelector((state) => state.appModule)
  const attendees = request.attendees || []
  // Lab requests carry the equipment booked alongside the room; classroom
  // requests come back with an empty list, so the section self-hides.
  const equipments = request.equipments || []
  // An approved booking whose slot has come and gone is done with — it reads as
  // "Completed" and loses its Cancel button, matching the Cowork booking card
  // above (same helper, so both modules agree on the cutoff: it trips 30 minutes
  // before the start, which is also the point past which cancelling is refused).
  const isCompleted =
    request.status === 'Approved' &&
    !!request.start_time &&
    checkFor30MinTimeBeforeBooking(request.start_time)
  const statusText = isCompleted ? 'Completed' : request.status
  const isCancellable =
    request.status !== 'Rejected' && request.status !== 'Cancelled' && !isCompleted
  // Same edit form as the Eduspace calendar. Under-review requests just change;
  // an approved one goes back for review on save (the backend decides). Not
  // once the slot is upon us (same 30-minute cutoff as cancelling), and not a
  // split booking — the form only edits one contiguous slot, so it would drop
  // the other run.
  const isEditable =
    selectedModule === MODULES.EDUSPACE &&
    !!onEdit &&
    (request.status === 'Under Review' || request.status === 'Approved') &&
    !!request.start_time &&
    !checkFor30MinTimeBeforeBooking(request.start_time) &&
    slotRunsFor(request).length <= 1
  const statusBadge = isCompleted
    ? { green: true }
    : request.status === 'Approved' ? { green: true } : request.status === 'Rejected' ? { red: true } : { yellow: true }

  // Clear the input once a post succeeds.
  useEffect(() => {
    if (flipped && commentPostSuccess) setCommentText('')
  }, [flipped, commentPostSuccess])

  const handlePost = () => {
    const text = commentText.trim()
    if (!text) return
    onPostComment(text)
  }

  return (
    // Override the room-card flip shell's fixed min-heights so the request card
    // sizes to its content (the front face is in normal flow and drives the
    // height; the comments back face overlays at that same height).
    <FlipCardScene active={flipped} style={{ minHeight: 'auto', maxHeight: 'none' }}>
      <FlipCardInner flipped={flipped} style={{ minHeight: 360 }}>
        {/* Front Side */}
        <FrontFace style={{ position: 'relative' }}>
          <Div
            Height={'100%'}
            borderRadius={30}
            padding={'30px 20px'}
            overflow={'visible'}
            style={{
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              border: `1px solid ${globalTheme.colors.nuetral2}`,
              background: globalTheme.colors.white1,
            }}
          >
            {/* Ticket number sits beside the room name — it is the reference an
                admin quotes back about a request, so the requester needs it on
                their own card, not only in the admin queue. `noWrap` + a
                non-shrinking chip keep a long room name from pushing it out. */}
            <Flexbox spaceBW alignCenter gap={10} noWrap>
              <Text font18 inter medium black3 style={{ wordBreak: 'break-word' }}>
                {request.room?.room_name}
              </Text>
              {request.ticket_id && (
                <Text
                  font12
                  inter
                  semiBold
                  nuetral6
                  style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  title={`Ticket #${request.ticket_id}`}
                >
                  #{request.ticket_id}
                </Text>
              )}
            </Flexbox>
            {request.meeting_title && (
              <Text
                font14
                inter
                regular
                nuetral6
                style={{ marginTop: 6, wordBreak: 'break-word' }}
                title={request.meeting_title}
              >
                {request.meeting_title}
              </Text>
            )}
            <Flexbox className='mt16 mb20' gap={8} alignCenter>
              <Location />
              <Text font14 inter regular grey2>Tower {request.room?.tower}</Text>
            </Flexbox>
            <WrapperBox borderRadius={50} padding='16px' borderColor={globalTheme.colors.nuetral2} background={globalTheme.colors.grey8}>
              <Flexbox gap={6} alignCenter wrap>
                <Text font16 inter medium nuetral13>Slot booked for:</Text>
                {/* Times come from the request's 30-minute slot rows, so a
                    booking of non-adjacent slots lists both ranges instead of
                    one span that swallows the gap between them. */}
                <Text font16 inter semiBold black3>
                  {moment.utc(request.start_time).format('MMM DD')} • {formatBookingTime(request)}
                </Text>
              </Flexbox>
            </WrapperBox>

            {attendees.length > 0 && (
              <>
                <Text font14 inter medium black3 className='mt20 mb12'>Participants</Text>
                <Flexbox wrap gap={8}>
                  {attendees.map((a, i) => (
                    <Badge key={i} blue small borderRadius={'20px'} statusText={attendeeName(a)} />
                  ))}
                </Flexbox>
              </>
            )}

            {equipments.length > 0 && (
              <>
                <Text font14 inter medium black3 className='mt20 mb12'>Equipment</Text>
                <Flexbox wrap gap={8}>
                  {/* Only the first two are shown so a lab booking with a long
                      equipment list can't push the card out of the grid; the
                      remainder live in the +N tooltip. */}
                  {equipments.slice(0, 2).map((e, i) => (
                    <span key={e?.id || i} title={equipmentName(e)}>
                      <Badge secondary small borderRadius={'20px'} statusText={equipmentName(e)} />
                    </span>
                  ))}
                  {equipments.length > 2 && (
                    <span title={equipments.slice(2).map(equipmentName).join(', ')}>
                      <Badge secondary small borderRadius={'20px'} statusText={`+${equipments.length - 2}`} />
                    </span>
                  )}
                </Flexbox>
              </>
            )}

            {/* Action area follows the content directly. */}
            <Div overflow="visible">
              <Flexbox className='mt20' spaceBW alignCenter>
                <Text font14 inter medium black3>Status</Text>
                <Badge statusText={statusText} {...statusBadge} borderRadius={'50px'} />
              </Flexbox>

              <Button
                name='View Comments'
                icon={<CommentIcon />}
                revIcon
                buttonMd
                primaryOutlined
                fullBtn
                className='mt16'
                onClick={() => onFlip(request.id)}
              />

              {isEditable && (
                <Button onClick={() => onEdit(request)} name='Edit Booking' buttonLg fullBtn primaryOutlined className='mt12' />
              )}

              {isCancellable && (
                <Button onClick={() => onCancel(request.id)} name='Cancel Booking' buttonLg fullBtn primaryOutlined className='mt12' />
              )}
            </Div>
          </Div>
        </FrontFace>

        {/* Back Side — comments. Only mounted while flipped. */}
        {flipped && (
          <BackFace>
            <Div
              Height={'100%'}
              borderRadius={30}
              padding={'20px'}
              overflow={'visible'}
              style={{
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                border: `1px solid ${globalTheme.colors.nuetral2}`,
                background: globalTheme.colors.white1,
                boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Flexbox spaceBW alignCenter>
                <Flexbox column gap={2} maxWidth='max-content'>
                  <Text font16 inter medium black3>Comments</Text>
                  <Text font12 inter regular grey2>{request.room?.room_name}</Text>
                </Flexbox>
                <CloseCircle onClick={onClose} className='pointer' />
              </Flexbox>

              <Breaker marginTop={12} marginBottom={12} />

              {/* Scrollable comment list. `flex: 1` already makes it take every
                  pixel the fixed-height footer doesn't, so shrinking the post box
                  is what enlarges it. minHeight MUST stay 0: a floor here can
                  exceed the card's height (the back face is sized by the front
                  face) and pushes the buttons out through the bottom edge. */}
              <Flexbox
                column
                gap={16}
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  alignContent: 'flex-start',
                }}
              >
                {isCommentsFetching ? (
                  <Flexbox justiCenter alignCenter style={{ width: '100%', padding: '20px 0' }}>
                    <Text font13 inter regular grey2>Loading comments…</Text>
                  </Flexbox>
                ) : (comments && comments.length > 0) ? (
                  comments.map((c, i) => <CommentRow key={c.id || i} comment={c} />)
                ) : (
                  <Flexbox justiCenter alignCenter style={{ width: '100%', padding: '20px 0' }}>
                    <Text font13 inter regular grey2>No comments yet. Be the first to comment.</Text>
                  </Flexbox>
                )}
              </Flexbox>

              {/* Pinned post box */}
              <Div overflow="visible" style={{ flexShrink: 0, marginTop: 12 }}>
                {/* StyledTextarea hardcodes height: 128px, so `rows` alone does
                    nothing — the explicit height is what actually shrinks it.
                    Radius is half the height, so the box reads as a pill like the
                    "Purpose of booking" input rather than a plain rectangle. */}
                <InputField
                  noLabel
                  type='textarea'
                  rows={2}
                  height='68px'
                  borderRadius='34px'
                  placeholder='Write a comment…'
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={() => {}}
                />
                {/* buttonMd, not Lg: a shorter footer is less likely to overrun
                    the card and leaves the thread more room. */}
                <Flexbox gap={12} alignCenter className='mt12'>
                  <Button buttonMd fullBtn primaryOutlined name='Back' onClick={onClose} />
                  <Button
                    buttonMd
                    fullBtn
                    primaryFilled
                    primary
                    name={isPostingComment ? 'Posting…' : 'Post Comment'}
                    disabled={isPostingComment || !commentText.trim()}
                    onClick={handlePost}
                  />
                </Flexbox>
              </Div>
            </Div>
          </BackFace>
        )}
      </FlipCardInner>
    </FlipCardScene>
  )
})

const DashboardUser = () => {
  const dispatch = useDispatch()
  // Fetches go through this instead of `dispatch` so a re-fired request cancels
  // the one it supersedes (see useLatestRequest). Tab / date / filter / search
  // changes all hit the same endpoints, so duplicates were common here.
  const runLatest = useLatestRequest()

  // Single selector with shallowEqual — fewer subscriptions, fewer re-renders.
  const {
    availableClassroomsList,
    availableClassroomsCount,
    isClassroomSliceFetching,
    isClassroomSliceSmallFetching,
    isClassroomSliceSuccess,
    isClassroomSliceError,
    classroomSliceSuccessMessage,
    classroomSliceErrorMessage,
    myClassroomRequestsList,
    myClassroomRequestsCount,
    approvedRequestsList,
    towersList,
    selectedSchool,
    requestCommentsList,
    requestCommentsCount,
    isCommentsFetching,
    isPostingComment,
    commentPostSuccess,
    isMyRequestsFetching,
  } = useSelector(classroomData, shallowEqual)

  const {
    coworkUsers,
    equipmentList,
    myBookingsList,
    myBookingCount,
    isCoworkSliceSuccess,
    coworkSliceSuccessMessage,
    isCoworkSliceError,
    coworkSliceErrorMessage,
    isMyBookingsFetching,
  } = useSelector(coworkData, shallowEqual)
  const { selectedModule } = useSelector((state) => state.appModule)
  const isCowork = selectedModule === MODULES.COWORK

  const [tab, setTab] = useState(0)
  // My Bookings sub-filter — sent to the bookings API as `status`.
  const [eventStatus, setEventStatus] = useState('Upcoming')
  const [flippedCard, setFlippedCard] = useState(null)
  // Which booking-request card is flipped to its comments back face (request id).
  const [flippedRequestId, setFlippedRequestId] = useState(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState('')
  const [calendarDate, setCalendarDate] = useState(moment().format('YYYY-MM-DD'))
  // The date picker is shared with the All Rooms tab, where it defaults to today
  // because availability is always asked for a specific day. My Bookings must
  // NOT inherit that default — it would silently hide every booking that isn't
  // today — so the date only filters this tab once the user actually picks one.
  const [dateFilterActive, setDateFilterActive] = useState(false)
  const myBookingsDate = dateFilterActive ? calendarDate : ''
  const [bookingLoading, setBookingLoading] = useState(false)
  // Makers Lab bookings go through a confirmation popup (equipment + instructor)
  // before they're submitted; `pendingLabBooking` holds the validated request.
  const [labPopupOpen, setLabPopupOpen] = useState(false)
  const [pendingLabBooking, setPendingLabBooking] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [loadMoreError, setLoadMoreError] = useState(false)
  // Marks an in-flight "load more" so the shared error flag can be attributed
  // to pagination (vs. a booking/other request) and pause the infinite scroll.
  const loadingMoreRef = useRef(false)
  // Same pair again for the My Bookings tab. Kept separate from the All Rooms
  // ones so a failed page on one tab can't leave the other tab's scroll paused.
  const [myBookingsLoadMoreError, setMyBookingsLoadMoreError] = useState(false)
  const myBookingsLoadingMoreRef = useRef(false)
  const [perPageDropName, setPerPageDropName] = useState(10)
  const [active, setActive] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState([])
  const [selectedEquipment, setSelectedEquipment] = useState([])
  const [capacity, setCapacity] = useState('')
  const [tower, setTower] = useState('')
  // `searchInput` is the raw, immediate text; `searchQuery` is the debounced
  // value actually sent to the API. One shared box searches the active tab.
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({
    capacity: '',
    tower: '',
    category: '',
    equipment: '',
  })

  const handleSelectOption = (option) => {
    const updatedFilters = { ...filters }
    switch (option.type) {
      case 'capacity':
        setCapacity(option.value)
        updatedFilters.capacity = [option.value]
        break
      case 'tower':
        setTower(option.name)
        updatedFilters.tower = [option.name]
        break
      case 'category':
        setSelectedCategory(option.name)
        updatedFilters.category = [option.value]
        break
      case 'equipment':
        setSelectedEquipment(option.name)
        updatedFilters.equipment = [option.value]
        break
      default:
        break
    }
    setFilters(updatedFilters)
    setActive(false)
  }

  const rolesCookie = Cookies.get('roles')
  const rolesArray = rolesCookie ? JSON.parse(rolesCookie) : []

  const isStudent = rolesArray.includes('Cowork Student')
  const isAdmin = rolesArray.includes('Cowork Admin')
  const isULBooking = rolesArray.includes('Cowork UL Booking')
  const isStaff = rolesArray.includes('Cowork Staff')
  const isFaculty = rolesArray.includes('Cowork Faculty')

  // Booking window: in Eduspace a Class User may book 1 month out, the
  // Operations Team (Class Admin) 6; every other module is capped at 3 months.
  const isClassAdmin = isModuleAdmin(rolesArray, MODULES.EDUSPACE)
  const isEduspace = selectedModule === MODULES.EDUSPACE
  const maxBookingDate = useMemo(
    () => bookingWindowEnd(isClassAdmin, new Date(), isEduspace),
    [isEduspace, isClassAdmin]
  )
  const maxBookingDateMessage = bookingWindowMessage(isClassAdmin, isEduspace)
  // const isStudent = rolesArray.includes('Cowork Lab Student')
  // const isAdmin = rolesArray.includes('Cowork Lab Admin')

  // Restore the old RoomCard rule: within CoWork, Student/Staff/Faculty may hold
  // only ONE slot at a time; Cowork Admin and UL Booking keep multi-slot
  // selection. Non-CoWork modules (Lab/Eduspace/Bloomberg) stay multi-slot, as
  // they were before.
  const coworkSingleSlot =
    selectedModule === MODULES.COWORK && !(isAdmin || isULBooking)

  const showBookingRoles =
    isAdmin ||
    isULBooking;
    // rolesArray.includes('Cowork Staff') ||
    // rolesArray.includes('Cowork Faculty') ||
    // rolesArray.includes('Cowork Class Admin') ||
    // rolesArray.includes('Cowork Lab Admin') ||
    // rolesArray.includes('BMB Admin')

  
  const bmbUsedMinutes = useMemo(() => {
    if (selectedModule !== MODULES.BLOOMBERG) return 0
    const raw = Cookies.get('loginData')
    const myId = raw ? JSON.parse(raw)?.id : null
    if (!myId) return 0
    return (approvedRequestsList || []).reduce((sum, r) => {
      if (r?.user_id !== myId) return sum
      if (r?.status !== 'Approved' && r?.status !== 'Under Review') return sum
      if (moment.utc(r.start_time).format('YYYY-MM-DD') !== calendarDate) return sum
      return sum + Math.max(0, moment.utc(r.end_time).diff(moment.utc(r.start_time), 'minutes'))
    }, 0)
  }, [approvedRequestsList, calendarDate, selectedModule])

  // Infinity for non-Bloomberg modules → the cap never applies there.
  const bmbRemainingMinutes =
    selectedModule === MODULES.BLOOMBERG ? Math.max(0, 60 - bmbUsedMinutes) : Infinity

  const hasData = availableClassroomsList?.length > 0
  const showSkeleton = isClassroomSliceFetching && !hasData

  // Same rule for the My Bookings tab, against whichever list that module shows.
  // `&& !length` keeps it to the FIRST load: a refetch after cancelling a
  // booking must not blank the cards the user is looking at, and the appended
  // infinite-scroll pages get their own trailing skeletons below.
  const myBookingsHasData = isCowork
    ? myBookingsList?.length > 0
    : myClassroomRequestsList?.length > 0
  const showMyBookingsSkeleton =
    (isCowork ? isMyBookingsFetching : isMyRequestsFetching) && !myBookingsHasData

  useEffect(() => {
    dispatch(getAllCoworkUsers({ isLabStudent: isStudent, isBmbStudent: selectedModule === MODULES.BLOOMBERG }))
  }, [])

  // Same structure as the MeetingRooms tower filter — { dropmenu, value }
  // shape consumed by ActionMenu.
  const towerOptions = useMemo(() => {
    const fallback = [
      "Tower A",
      "Tower C",
      "CDS - 1st Floor",
      "CDS - 07th Floor",
      "CDS - 08th Floor",
      "402",
      "383 Floor 1",
      "383 Floor 2",
      "383 Floor 3",
      "383 Floor 4",
      "LPU Block 38 (Level 3)",
    ].map((name) => ({ dropmenu: name, value: name }))
    const fromList =
      (towersList || []).length > 0
        ? towersList.map((t) => ({ dropmenu: t?.tower, value: t?.id }))
        : fallback
    return [{ dropmenu: "All Locations", value: "" }, ...fromList]
  }, [towersList])

  // Compute once per coworkUsers change (was recomputed every render).
  const participantOptions = useMemo(() => {
    return (coworkUsers || []).map((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.trim()
      const truncated =
        fullName.length > 20
          ? fullName.slice(0, 20).split(' ').slice(0, -1).join(' ') + '...'
          : fullName
      return { cat: user.id, key: truncated }
    })
  }, [coworkUsers])

  const getAvailableClassroomsLocal = () => {
    runLatest(getAvailableClassrooms, {
      perPageCount: perPageDropName,
      pageNumber: 1,
      searchValue: searchQuery,
      filters,
      meetingDate: calendarDate,
      isStudent: selectedModule === MODULES.COWORK && isStudent,
      isAdmin: selectedModule === MODULES.COWORK && isAdmin,
      isClassAdmin: selectedModule === MODULES.EDUSPACE,
      isLab: selectedModule === MODULES.MAKERSLAB,
      isBmb: selectedModule === MODULES.BLOOMBERG,
    })
    selectedModule !== MODULES.COWORK && runLatest(getApprovedClassroomRequests, {
      isLab : selectedModule === MODULES.MAKERSLAB,
      isBmb : selectedModule === MODULES.BLOOMBERG,
    })
    setCurrentPage(1)
    setLoadMoreError(false)
    loadingMoreRef.current = false
  }

  // Fetch the next page and append it to the existing list (slice appends).
  // The page is derived from how many rooms are already loaded so a failed
  // page is retried rather than skipped.
  const loadMoreClassrooms = () => {
    setLoadMoreError(false)
    loadingMoreRef.current = true
    const nextPage =
      Math.floor((availableClassroomsList?.length || 0) / perPageDropName) + 1
    runLatest(getAvailableClassrooms, {
      perPageCount: perPageDropName,
      pageNumber: nextPage,
      searchValue: searchQuery,
      filters,
      meetingDate: calendarDate,
      isStudent: selectedModule === MODULES.COWORK && isStudent,
      isAdmin: selectedModule === MODULES.COWORK && isAdmin,
      isClassAdmin: selectedModule === MODULES.EDUSPACE,
      isLab: selectedModule === MODULES.MAKERSLAB,
      isBmb: selectedModule === MODULES.BLOOMBERG,
    })
    setCurrentPage(nextPage)
  }

  // A successful append clears the in-flight marker.
  useEffect(() => {
    loadingMoreRef.current = false
  }, [availableClassroomsList?.length])

  const hasMoreClassrooms =
    availableClassroomsList?.length > 0 &&
    availableClassroomsList?.length < availableClassroomsCount
  const classroomsSentinelRef = useInfiniteScroll({
    hasMore: hasMoreClassrooms && !loadMoreError,
    isLoading: isClassroomSliceSmallFetching,
    loadMore: loadMoreClassrooms,
  })

  // Pull the "My Bookings" list from the source that matches the active module:
  // Cowork → cowork room bookings (getMyBookings); MakersLab/Eduspace →
  // classroom requests (getMyClassroomRequests). The cowork slice appends, so
  // clear it before each fresh fetch.
  const getMyBookingsLocal = () => {
    dispatch(clearCoworkSliceData())
    runLatest(getMyBookings, {
      perPageCount: MY_BOOKINGS_PAGE_SIZE,
      pageNumber: 1,
      searchValue: searchQuery,
      filters: { tower: filters.tower || '', status: '' },
      status: eventStatus,
    })
    setMyBookingsLoadMoreError(false)
    myBookingsLoadingMoreRef.current = false
  }

  // Page 1 of the classroom/lab request list — the non-Cowork half of My
  // Bookings. Same shape as the Cowork fetch above so both can be paged by
  // loadMoreMyBookings without either one owning the paging rules.
  const getMyClassroomRequestsLocal = (pageNumber = 1) =>
    runLatest(getMyClassroomRequests, {
      pageNumber,
      perPageCount: MY_BOOKINGS_PAGE_SIZE,
      eventStatus,
      searchQuery,
      // Keep the active date filter across every page, or scrolling would
      // silently widen the list back to every date.
      date: myBookingsDate,
      isLab: selectedModule === MODULES.MAKERSLAB,
      isBmb: selectedModule === MODULES.BLOOMBERG,
    })

  // ---- My Bookings infinite scroll ----
  // Both slices APPEND their page, and both report a total count, so the two
  // sources page identically — only the thunk differs.
  const myBookingsLoaded =
    (isCowork ? myBookingsList?.length : myClassroomRequestsList?.length) || 0
  const myBookingsTotal = (isCowork ? myBookingCount : myClassroomRequestsCount) || 0
  const isMyBookingsLoading = isCowork ? isMyBookingsFetching : isMyRequestsFetching
  const hasMoreMyBookings = myBookingsLoaded > 0 && myBookingsLoaded < myBookingsTotal

  // Derive the page from how many rows are loaded (not from a counter) so a
  // page that failed is retried rather than skipped — same rule as the rooms
  // list above.
  const loadMoreMyBookings = () => {
    setMyBookingsLoadMoreError(false)
    myBookingsLoadingMoreRef.current = true
    const nextPage = Math.floor(myBookingsLoaded / MY_BOOKINGS_PAGE_SIZE) + 1
    if (isCowork) {
      runLatest(getMyBookings, {
        perPageCount: MY_BOOKINGS_PAGE_SIZE,
        pageNumber: nextPage,
        searchValue: searchQuery,
        filters: { tower: filters.tower || '', status: '' },
        status: eventStatus,
      })
    } else {
      getMyClassroomRequestsLocal(nextPage)
    }
  }

  // A successful append clears the in-flight marker.
  useEffect(() => {
    myBookingsLoadingMoreRef.current = false
  }, [myBookingsLoaded])

  const myBookingsSentinelRef = useInfiniteScroll({
    hasMore: hasMoreMyBookings && !myBookingsLoadMoreError,
    isLoading: isMyBookingsLoading,
    loadMore: loadMoreMyBookings,
  })

  // Debounce the search box → only commit to `searchQuery` (which drives the
  // API call) 400ms after the user stops typing.
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Fetch only the active tab's data, so the shared search box searches
  // available rooms on "All Labs" and bookings on "My Bookings". `tab` is a
  // dependency so switching tabs re-applies the current search to the new tab.
  useEffect(() => {
    if (!calendarDate) return
    if (tab === 0) {
      dispatch(clearClassroomSliceData())
      getAvailableClassroomsLocal()
    } else if (isCowork) {
      getMyBookingsLocal()
    } else {
      getMyClassroomRequestsLocal(1)
      setMyBookingsLoadMoreError(false)
      myBookingsLoadingMoreRef.current = false
    }
    return () => {
      dispatch(clearClassroomSliceStates())
      dispatch(clearClassroomSliceData())
    }
  }, [tab, searchQuery, perPageDropName, filters, calendarDate, myBookingsDate, selectedModule, eventStatus])

  const handleRequestSlot = useCallback(
    (room, { slots, title, participants, duration, isEvent = false, seatCount = 1 }) => {
      if (!slots || slots.length === 0) {
        toast.error('Please select at least one time slot to book.', toastOpts('booking-no-slot'))
        return
      }
      if (!title?.trim()) {
        toast.error('Please enter a title for this booking.', toastOpts('booking-no-title'))
        return
      }

      const sortedSlots = [...slots].sort()
      const slotsToSend = mergeSlots(sortedSlots)
      // Backstop for the calendar's maxDate: a date picked before the role was
      // resolved, or left stale by a module switch, must not slip through.
      if (isBeyondBookingWindow(calendarDate, isClassAdmin, new Date(), isEduspace)) {
        toast.error(maxBookingDateMessage, toastOpts('booking-window'))
        return
      }

      const attendees = (participants || []).map((p) => ({
        id: p.cat,
        name: (p.key || '').trim(),
        value: p.cat,
        label: (p.key || '').trim(),
      }))

      // CoWork → DIRECT bookings (no request/approval), role-routed exactly like
      // the old ConfirmRoomBookingPopup: Admin / UL Booking book their merged
      // multi-slot selection via the admin endpoint; Student / Staff / Faculty
      // book their single selected slot. No school needed for CoWork.
      if (selectedModule === MODULES.COWORK) {
        if (isAdmin || isULBooking) {
          setBookingLoading(true)
          dispatch(
            postRoomBookingAdmin({
              room_id: room.id,
              meetingDate: calendarDate,
              selectedAttendees: attendees,
              meetingSlot: slotsToSend,
              meetingDetail: title.trim(),
            })
          )
        } else if (isStudent || isStaff || isFaculty) {
          setBookingLoading(true)
          dispatch(
            postRoomBookings({
              room_id: room.id,
              meetingDate: calendarDate,
              selectedAttendees: attendees,
              // Single-slot roles hold exactly one selected slot.
              meetingSlot: sortedSlots[0],
              meetingDetail: title.trim(),
            })
          )
        } else {
          toast.error('Your role does not have permission to book CoWork rooms.', toastOpts('booking-no-permission'))
        }
        return
      }

      const schoolForRequest =
        typeof selectedSchool === 'string'
          ? selectedSchool
          : selectedSchool?.payload ?? selectedSchool
      const schoolResolved = schoolForRequest ?? (selectedModule === MODULES.EDUSPACE ? null : "Masters Union")
      if (!schoolResolved) {
        toast.error('Please select a school for this booking.', toastOpts('booking-no-school'))
        return
      }
      // The picker carries the display label, but bookings are keyed off the school
      // FK — send both. Non-Eduspace modules ("Masters Union") have no id, so null.
      const schoolIdResolved = SchoolIdsMap[schoolResolved] || null

      const request = {
        room_id: room.id,
        date: calendarDate,
        slot: slotsToSend,
        meeting_title: title.trim(),
        school: schoolResolved,
        school_id: schoolIdResolved,
        attendees,
        // Eduspace-only flag; the card forces false for every other module.
        isEvent: selectedModule === MODULES.EDUSPACE ? !!isEvent : false,
        
        participants: roomIsCapacityBased(room) ? Math.max(1, seatCount) : 1,
      }

      // Makers Lab → confirm equipment + instructor in a popup before booking.
      if (selectedModule === MODULES.MAKERSLAB) {
        setPendingLabBooking({ request, room })
        setLabPopupOpen(true)
        return
      }

      setBookingLoading(true)
      dispatch(
        createClassroomRequest({
          ...request,
          equipments: room.equipments?.map((e) => e.id || e) || [],
          needInstructor: false,
        })
      )
    },
    [dispatch, selectedSchool, isStudent, isAdmin, isULBooking, isStaff, isFaculty, calendarDate, selectedModule, isClassAdmin]
  )

  // Submit the pending Makers Lab booking once equipment + instructor are chosen.
  const handleConfirmLabBooking = useCallback(
    ({ equipments, needInstructor }) => {
      if (!pendingLabBooking) return
      setBookingLoading(true)
      dispatch(
        createClassroomRequest({
          ...pendingLabBooking.request,
          equipments,
          needInstructor,
        })
      )
    },
    [dispatch, pendingLabBooking]
  )

  // Classroom / Lab / Bloomberg requests cancel by REQUEST id — the endpoint
  // cancels the request and its confirmed booking together, which is what frees
  // a Bloomberg seat. (This used to dispatch the cowork booking-cancel thunk with
  // a request id under the wrong key, so it called /cancelBooking/undefined.)
  const handleCancelBooking = (selectedRequestId) => {
    dispatch(cancelClassroomRequest({ requestId: selectedRequestId }))
  }

  // ---- Edit a booking request — the Eduspace calendar's edit form, prefilled.
  // Times on a request are wall-clock values stored as UTC (the card formats
  // them with moment.utc), so read the hours the same way.
  const [editSel, setEditSel] = useState(null)
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const isModuleAdminUser = isModuleAdmin(rolesArray, selectedModule)

  const handleOpenEdit = useCallback((request) => {
    const start = moment.utc(request.start_time)
    const end = moment.utc(request.end_time)
    // Prefer the FK; fall back to the stored label, mapped through the same
    // label → id table the booking form uses ("Exec Education" etc. don't match
    // SCHOOL_OPTIONS by name). Anything that still isn't an Eduspace school
    // (e.g. "Masters Union" on other modules) leaves the picker empty.
    const schoolId = canonicalSchoolId(
      request.school_id || SchoolIdsMap[request.School] || request.School
    )
    const school = SCHOOL_OPTIONS.some((o) => o.value === schoolId) ? schoolId : ''
    setEditSel({
      requestId: request.id,
      roomId: request.room_id ?? request.room?.id,
      roomName: request.room?.room_name,
      date: start.format('YYYY-MM-DD'),
      start: start.hour() + start.minute() / 60,
      end: end.hour() + end.minute() / 60,
      title: request.meeting_title || '',
      school,
      attendees: request.attendees || [],
      isEvent: !!request.isEvent,
      needInstructor: !!(request.needInstructor ?? request.instructor),
      // An admin's edit keeps the booking approved; anyone else's sends it back.
      wasApproved: request.status === 'Approved' && !isModuleAdminUser,
    })
  }, [isModuleAdminUser])

  // Popup stays open (loading) until the update settles, so a failure leaves the
  // form filled in. The slice's error flag already toasts the API message.
  const handleEditConfirm = async ({ meeting_title, attendees, school, needInstructor, isEvent, start, end }) => {
    const sel = editSel
    if (!sel) return
    const toHHMM = (h) => {
      const hh = Math.floor(h)
      const mm = Math.round((h - hh) * 60)
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
    }
    setIsSavingEdit(true)
    try {
      const res = await dispatch(
        updateClassroomRequest({
          requestId: sel.requestId,
          room_id: sel.roomId,
          date: sel.date,
          slot: [`${toHHMM(start)} - ${toHHMM(end)}`],
          meeting_title,
          // School and the event flag are Eduspace-only; other modules keep
          // whatever the request already has (the backend skips omitted fields).
          ...(isEduspace
            ? {
                school: SCHOOL_OPTIONS.find((o) => o.value === school)?.name,
                school_id: school || null,
              }
            : {}),
          attendees,
          equipments: [],
          needInstructor,
          isEvent: isEduspace ? isEvent : false,
        })
      ).unwrap()
      toast.success(
        res?.message === 'Booking request updated and sent for admin approval'
          ? 'Booking updated and sent for admin approval.'
          : 'Booking request updated.',
        toastOpts('request-edited')
      )
      setEditSel(null)
      dispatch(clearClassroomSliceStates())
      // Status / slot may have changed — reload the list from page 1.
      dispatch(clearClassroomSliceData())
      getMyClassroomRequestsLocal(1)
      setMyBookingsLoadMoreError(false)
      myBookingsLoadingMoreRef.current = false
    } catch (e) {
      // keep the popup open; the error effect surfaces the message
    } finally {
      setIsSavingEdit(false)
    }
  }

  // Cowork bookings cancel via the cowork slice ({ id }); the slice updates the
  // booking in place, so no refetch is needed.
  const handleCancelCoworkBooking = (bookingId) => {
    dispatch(cancelABooking({ id: bookingId }))
  }

  // Toast on a successful cowork booking cancellation.
  useEffect(() => {
    if (isCowork && isCoworkSliceSuccess && coworkSliceSuccessMessage === 'Booking Cancelled Successfully') {
      toast.success('Booking cancelled.', toastOpts('booking-cancelled'))
      dispatch(clearCoworkSliceStates())
    }
  }, [isCowork, isCoworkSliceSuccess, coworkSliceSuccessMessage])

  // Direct CoWork booking succeeded (postRoomBookings / postRoomBookingAdmin) —
  // toast, close the card, and land on My Bookings (the tab effect refetches).
  useEffect(() => {
    if (isCowork && isCoworkSliceSuccess && coworkSliceSuccessMessage === 'Room Booked Successfully') {
      toast.success('Room booked successfully.', toastOpts('booking-created'))
      setBookingLoading(false)
      setFlippedCard(null)
      dispatch(clearCoworkSliceStates())
      setTab(1)
    }
  }, [isCowork, isCoworkSliceSuccess, coworkSliceSuccessMessage])

  // CoWork booking (or other cowork call) failed — surface the API message and
  // unblock the Request/Book button.
  useEffect(() => {
    if (isCowork && isCoworkSliceError && coworkSliceErrorMessage) {
      toast.error(coworkSliceErrorMessage || 'Your booking could not be completed. Please try again.', toastOpts('booking-error'))
      setBookingLoading(false)
      // Only pause My Bookings' infinite scroll when the failure came from one
      // of its "load more" pages — this flag is shared with bookings/cancels.
      if (myBookingsLoadingMoreRef.current) {
        setMyBookingsLoadMoreError(true)
        myBookingsLoadingMoreRef.current = false
      }
      dispatch(clearCoworkSliceStates())
    }
  }, [isCowork, isCoworkSliceError, coworkSliceErrorMessage])

  useEffect(() => {
    if (
      isClassroomSliceSuccess &&
      classroomSliceSuccessMessage === 'Classroom booking request created successfully'
    ) {
      toast.success('Booking request submitted for approval.', toastOpts('request-created'))
      setBookingLoading(false)
      setLabPopupOpen(false)
      setPendingLabBooking(null)
      setFlippedCard(null)
      dispatch(clearClassroomSliceStates())
      // Switch to My Bookings — the tab-driven fetch effect clears the list and
      // refetches it once. Don't also fetch here: the slice appends, so a second
      // fetch would duplicate the just-created booking until a refresh.
      setTab(1)
    }
  }, [isClassroomSliceSuccess, classroomSliceSuccessMessage])

  // Cancelling frees the slot (and, on Bloomberg, its seats), so refetch the list
  // rather than leaving a stale row showing the old status.
  useEffect(() => {
    if (isClassroomSliceSuccess && classroomSliceSuccessMessage === 'Booking cancelled successfully') {
      toast.success('Booking cancelled.', toastOpts('request-cancelled'))
      dispatch(clearClassroomSliceStates())
      dispatch(clearClassroomSliceData())
      // Back to page 1 — the list was just emptied, so the scroll starts over.
      getMyClassroomRequestsLocal(1)
      setMyBookingsLoadMoreError(false)
      myBookingsLoadingMoreRef.current = false
    }
  }, [isClassroomSliceSuccess, classroomSliceSuccessMessage])

  useEffect(() => {
    if (isClassroomSliceError && classroomSliceErrorMessage) {
      toast.error(classroomSliceErrorMessage || 'Your request could not be submitted. Please try again.', toastOpts('request-error'))
      setBookingLoading(false)
      // Only pause the infinite scroll when the failure came from a "load more"
      // page — not from a booking or other request sharing this error flag.
      if (loadingMoreRef.current) {
        setLoadMoreError(true)
        loadingMoreRef.current = false
      }
      // Same for the My Bookings tab, which pages the classroom-requests list
      // through this very same error flag.
      if (myBookingsLoadingMoreRef.current) {
        setMyBookingsLoadMoreError(true)
        myBookingsLoadingMoreRef.current = false
      }
      dispatch(clearClassroomSliceStates())
    }
  }, [isClassroomSliceError, classroomSliceErrorMessage])

  const clearSearch = () => {
    setSearchInput('')
    setSearchQuery('')
  }

  // Enter commits immediately (skips the debounce); typing goes through onChange.
  const submitSearch = (searchValue) => {
    const value = searchValue?.trim() || ''
    setSearchInput(value)
    setSearchQuery(value)
  }

  const handleFlip = useCallback((idx) => setFlippedCard(idx), [])
  const handleClose = useCallback(() => setFlippedCard(null), [])

  // Flip a booking-request card to its comments back face and load its comments.
  const handleFlipRequest = useCallback((requestId) => {
    setFlippedRequestId(requestId)
    dispatch(clearRequestComments())
    dispatch(getRequestComments({ requestId }))
  }, [dispatch])
  const handleCloseRequest = useCallback(() => {
    setFlippedRequestId(null)
    dispatch(clearRequestComments())
  }, [dispatch])
  const handlePostComment = useCallback((text) => {
    if (!flippedRequestId) return
    dispatch(addRequestComment({ requestId: flippedRequestId, comment: text }))
  }, [dispatch, flippedRequestId])

  return (
    <>
      <ConfirmLabBookingPopup
        showActive={labPopupOpen}
        room={pendingLabBooking?.room}
        isSubmitting={bookingLoading}
        onCancel={() => setLabPopupOpen(false)}
        onConfirm={handleConfirmLabBooking}
      />
      {/* Edit a booking request from My Bookings */}
      <CreateBookingPopup
        open={!!editSel}
        mode='edit'
        showEduspaceFields={isEduspace}
        selection={editSel}
        loading={isSavingEdit}
        onClose={() => {
          if (isSavingEdit) return // don't dismiss mid-request
          setEditSel(null)
        }}
        onConfirm={handleEditConfirm}
      />
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      
      <div>
      <WrapperBox borderRadius={80} borderColor={globalTheme.colors.nuetral2} boxShadow padding={'8px 12px'} overflow={'visible'} className='respToolbarShell'>
        <Flexbox spaceBW alignCenter className='respToolbar'>
          <WrapperBox borderRadius={80} borderColor={globalTheme.colors.nuetral2} background={globalTheme.colors.nuetral2} boxShadow padding={'8px'} maxWidth={'40%'} className='respTabShell'>
            <Flexbox gap={24} className='respTabRow'>
              <Button name='All Rooms' icon={<Funnel color={tab === 0 ? globalTheme.colors.black3 : globalTheme.colors.grey2} />} revIcon buttonMd {...(tab === 0 ? { whiteFilled: true } : '')} fullBtn onClick={() => setTab(0)} />
              <Button name='My Bookings' icon={<User color={tab === 1 ? globalTheme.colors.black3 : globalTheme.colors.grey2} />} revIcon buttonMd {...(tab === 1 ? { whiteFilled: true } : '')} fullBtn onClick={() => setTab(1)} />
            </Flexbox>
          </WrapperBox>
          <Flexbox gap={12} alignCenter flexEnd maxWidth='60%' className='respToolbarGroup'>

            <WrapperBox background={globalTheme.colors.grey8} padding={'10px'} borderRadius={50} borderColor={globalTheme.colors.grey6} maxWidth={'max-content'} maxHeight={'max-content'} className={'pointer'} onClick={() => setSearchOpen(true)}>
              <SearchBar
                clearSearch={clearSearch}
                placeholder={tab === 0 ? 'Search labs...' : 'Search bookings...'}
                onChange={setSearchInput}
                searchFunction={submitSearch}
              />
            </WrapperBox>

            <DatePicker
              value={calendarDate}
              maxDate={maxBookingDate}
              maxDateMessage={maxBookingDateMessage}
              onChange={(date) => {
                dispatch(clearClassroomSliceData())
                setCalendarDate(date)
                // An explicit pick — from here on the date filters My Bookings
                // too (cleared from the banner on that tab).
                setDateFilterActive(true)
              }}
            />

            <CustomDropdown
              isimp={false}
              dropName={"Capacity"}
              options={[
                { value: "2", type: "capacity", name: "2" },
                { value: "3", type: "capacity", name: "3" },
                { value: "4", type: "capacity", name: "4" },
                { value: "5", type: "capacity", name: "5" },
                { value: "6", type: "capacity", name: "6" },
                { value: "7", type: "capacity", name: "7" },
                { value: "8", type: "capacity", name: "8" },
                { value: "9", type: "capacity", name: "9" },
              ]}
              onSelect={handleSelectOption}
              isActive={active}
              setIsActive={setActive}
            />
            <WrapperBox
              background={globalTheme.colors.grey8}
              maxWidth="max-content"
              padding={'8px 12px'}
              borderRadius={50}
              borderColor={globalTheme.colors.grey6}
              maxHeight={'max-content'}
              className={'pointer'}
              overflow={'visible'}
            >
              <ActionMenu
                btnName="Tower"
                menuList={towerOptions}
                onSelect={(value) => {
                  setTower(typeof value === 'string' ? value : '')
                  setFilters((prev) => ({ ...prev, tower: value ? [value] : '' }))
                }}
              />
            </WrapperBox>
            <ModuleSwitcher />
          </Flexbox>
        </Flexbox>
      </WrapperBox>

      {/* ===== All Labs Tab ===== */}
      {tab == 0 &&
        <Flexbox wrap alignStart className='mt20' gap={24}>
          {flippedCard !== null && <BlurOverlay onClick={handleClose} />}
          {showSkeleton && [...Array(4)].map((_, i) => <SkeletonCard key={i} />)}

          {!showSkeleton && availableClassroomsList?.length === 0 && (
            <Flexbox justiCenter alignCenter style={{ width: '100%', padding: '40px 0' }}>
              <Text font16 inter regular grey2>No labs available for this date</Text>
            </Flexbox>
          )}
          {!showSkeleton && availableClassroomsList?.map((room, index) => (
            <RoomCard
              key={room.id || index}
              room={room}
              index={index}
              flipped={flippedCard === index}
              calendarDate={calendarDate}
              participantOptions={participantOptions}
              bookingLoading={bookingLoading}
              onFlip={handleFlip}
              onClose={handleClose}
              onRequest={handleRequestSlot}
              onDateChange={setCalendarDate}
              bmbRemainingMinutes={bmbRemainingMinutes}
              singleSlot={coworkSingleSlot}
              showBookingRoles={showBookingRoles}
              maxBookingDate={maxBookingDate}
              maxBookingDateMessage={maxBookingDateMessage}
            />
          ))}

          {/* Infinite scroll: loads more skeletons fill in as the next page arrives */}
          {!showSkeleton && isClassroomSliceSmallFetching && hasData &&
            [...Array(3)].map((_, i) => <SkeletonCard key={`more-${i}`} />)}

          {/* Sentinel — hidden while paused on error so it can't re-fire. */}
          {hasMoreClassrooms && !loadMoreError && (
            <div
              ref={classroomsSentinelRef}
              style={{ width: '100%', height: '1px' }}
            />
          )}

          {/* Pagination failed — pause auto-loading and let the user retry. */}
          {loadMoreError && !isClassroomSliceSmallFetching && (
            <Flexbox column alignCenter gap={12} className='mt20' style={{ width: '100%' }}>
              <Text font14 inter regular grey2>Couldn't load more labs.</Text>
              <Button
                primaryOutlined
                buttonMd
                name='Retry'
                onClick={loadMoreClassrooms}
                isCalling={isClassroomSliceSmallFetching}
              />
            </Flexbox>
          )}
        </Flexbox>
      }

      {/* ===== My Bookings Tab ===== */}
      {tab == 1 &&
        <>
          {/* Upcoming / Past switcher — sent to the bookings API as `status`. */}
          <Flexbox alignCenter className='mt20'>
            <WrapperBox
              borderRadius={80}
              background={globalTheme.colors.black4}
              borderColor={globalTheme.colors.black4}
              padding={'8px'}
              maxWidth={'max-content'}
              className='respTabShell'
            >
              <Flexbox gap={12} maxWidth='max-content' className='respTabRow'>
                <Button
                  name={<Span inter medium font14 color={eventStatus === 'Upcoming' ? globalTheme.colors.black4 : globalTheme.colors.white1}>Upcoming Bookings</Span>}
                  icon={<Funnel color={eventStatus === 'Upcoming' ? globalTheme.colors.black4 : globalTheme.colors.white1} />}
                  revIcon
                  buttonMd
                  bgColor={eventStatus === 'Upcoming' ? globalTheme.colors.white1 : 'transparent'}
                  onClick={() => setEventStatus('Upcoming')}
                />
                <Button
                  name={<Span inter medium font14 color={eventStatus === 'Past' ? globalTheme.colors.black4 : globalTheme.colors.white1}>Past Bookings</Span>}
                  icon={<User color={eventStatus === 'Past' ? globalTheme.colors.black4 : globalTheme.colors.white1} />}
                  revIcon
                  buttonMd
                  bgColor={eventStatus === 'Past' ? globalTheme.colors.white1 : 'transparent'}
                  onClick={() => setEventStatus('Past')}
                />
              </Flexbox>
            </WrapperBox>
          </Flexbox>

          <Flexbox gap={4} alignCenter className='mt20'>
            <Text font20 inter medium black4>{eventStatus} Bookings</Text>
            <Text font20 inter medium nuetral13> ({String((isCowork ? myBookingCount : myClassroomRequestsCount) || 0).padStart(2, '0')})</Text>
          </Flexbox>

          {/* The date picker lives in the shared toolbar, so say plainly that it
              is narrowing this list — and give it a way out that doesn't disturb
              the All Rooms tab's own use of that date. */}
          {!isCowork && dateFilterActive && (
            <Flexbox
              spaceBW
              alignCenter
              className='mt16'
              padding='10px 16px'
              borderRadius={50}
              background={globalTheme.colors.grey8}
              // Flexbox has no borderColor prop (it styles background/padding/
              // radius only), so the outline goes through style.
              style={{ border: `1px solid ${globalTheme.colors.nuetral2}` }}
            >
              <Text font14 inter medium black4>
                Showing bookings on {moment(calendarDate, 'YYYY-MM-DD').format('MMM DD, YYYY')}
              </Text>
              <Text
                font14
                inter
                semiBold
                className='pointer'
                onClick={() => setDateFilterActive(false)}
                style={{ color: globalTheme.colors.primary, textDecoration: 'underline', whiteSpace: 'nowrap' }}
              >
                Clear date
              </Text>
            </Flexbox>
          )}
          <Flexbox wrap className='mt20' gap={28}>
            {!isCowork && flippedRequestId !== null && <BlurOverlay onClick={handleCloseRequest} />}
            {showMyBookingsSkeleton ? (
              /* First load only. Without this the tab sat on "No bookings yet"
                 while the request was still in flight, which reads as "you have
                 none" rather than "still loading". */
              [...Array(3)].map((_, i) => <BookingSkeletonCard key={`mb-sk-${i}`} />)
            ) : isCowork ? (
              /* ===== Cowork room bookings ===== */
              myBookingsList?.length > 0 ? myBookingsList.map((booking, index) => {
                const isCompleted = booking.status === 'Booked' && checkFor30MinTimeBeforeBooking(booking.start_time)
                const statusText = isCompleted ? 'Completed' : booking.status
                const badgeProps = isCompleted
                  ? { green: true }
                  : booking.status === 'Booked'
                    ? { blue: true }
                    : booking.status === 'Cancelled'
                      ? { red: true }
                      : { yellow: true }
                const attendees = booking.room_bookings_attendees || []
                const isCancellable = booking.status === 'Booked' && !isCompleted
                return (
                  <WrapperBox borderRadius={30} key={booking.id || index} padding='30px 20px' borderColor={globalTheme.colors.nuetral2} maxWidth={'32%'} className='respCard3'>
                    {/* Same ticket chip as the request card. A cowork booking
                        only carries one when it came from a request, so it
                        self-hides rather than printing an empty "#". */}
                    <Flexbox spaceBW alignCenter gap={10} noWrap>
                      <Text font18 inter medium black3 style={{ wordBreak: 'break-word' }}>
                        {booking.room?.room_name}
                      </Text>
                      {(booking.room_booking_request?.ticket_id || booking.ticket_id) && (
                        <Text
                          font12
                          inter
                          semiBold
                          nuetral6
                          style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                          title={`Ticket #${booking.room_booking_request?.ticket_id || booking.ticket_id}`}
                        >
                          #{booking.room_booking_request?.ticket_id || booking.ticket_id}
                        </Text>
                      )}
                    </Flexbox>
                    {booking.meeting_title && (
                      <Text
                        font14
                        inter
                        regular
                        nuetral6
                        style={{ marginTop: 6, wordBreak: 'break-word' }}
                        title={booking.meeting_title}
                      >
                        {booking.meeting_title}
                      </Text>
                    )}
                    <Flexbox className='mt16 mb20' gap={8} alignCenter>
                      <Location />
                      <Text font14 inter regular grey2>Tower {booking.room?.tower}</Text>
                    </Flexbox>
                    <WrapperBox borderRadius={50} padding='16px' borderColor={globalTheme.colors.nuetral2} background={globalTheme.colors.grey8}>
                      <Flexbox gap={6} alignCenter wrap>
                        <Text font16 inter medium nuetral13>Slot booked for:</Text>
                        <Text font16 inter semiBold black3>
                          {/* UTC to match convertToSlot — booking times are stored naive-UTC, so
                              local formatting pushed evening slots onto the next calendar day. */}
                          {moment.utc(booking.start_time).format('MMM DD')} • {convertToSlot(booking.start_time, booking.end_time)}
                        </Text>
                      </Flexbox>
                    </WrapperBox>

                    {attendees.length > 0 && (
                      <>
                        <Text font14 inter medium black3 className='mt20 mb12'>Participants</Text>
                        <Flexbox wrap gap={8}>
                          {attendees.map((a, i) => (
                            <Badge key={i} blue small borderRadius={'20px'} statusText={attendeeName(a)} />
                          ))}
                        </Flexbox>
                      </>
                    )}

                    {isCancellable ? (
                      <Button onClick={() => handleCancelCoworkBooking(booking.id)} name='Cancel Booking' buttonLg fullBtn primaryOutlined className='mt30' />
                    ) : (
                      <Flexbox className='mt20' spaceBW alignCenter>
                        <Text font14 inter medium black3>Status</Text>
                        <Badge statusText={statusText} {...badgeProps} borderRadius={'50px'} />
                      </Flexbox>
                    )}
                  </WrapperBox>
                )
              }) : (
                <Flexbox justiCenter alignCenter style={{ width: '100%', padding: '40px 0' }}>
                  <Text font16 inter regular grey2>No bookings yet</Text>
                </Flexbox>
              )
            ) : (
              /* ===== Classroom / Lab requests — flip to comments ===== */
              myClassroomRequestsList?.length > 0 ? myClassroomRequestsList.map((request, index) => (
                <RequestCard
                  key={request.id || index}
                  request={request}
                  flipped={flippedRequestId === request.id}
                  comments={flippedRequestId === request.id ? requestCommentsList : null}
                  isCommentsFetching={isCommentsFetching}
                  isPostingComment={isPostingComment}
                  commentPostSuccess={commentPostSuccess}
                  onFlip={handleFlipRequest}
                  onClose={handleCloseRequest}
                  onCancel={handleCancelBooking}
                  onEdit={handleOpenEdit}
                  onPostComment={handlePostComment}
                />
              )) : (
                <Flexbox justiCenter alignCenter style={{ width: '100%', padding: '40px 0' }}>
                  <Text font16 inter regular grey2>No booking requests yet</Text>
                </Flexbox>
              )
            )}

            {/* Infinite scroll: trailing skeletons stand in for the page in flight */}
            {!showMyBookingsSkeleton && isMyBookingsLoading && myBookingsHasData &&
              [...Array(3)].map((_, i) => <BookingSkeletonCard key={`mb-more-${i}`} />)}

            {/* Sentinel — hidden while paused on error so it can't re-fire. */}
            {hasMoreMyBookings && !myBookingsLoadMoreError && (
              <div
                ref={myBookingsSentinelRef}
                style={{ width: '100%', height: '1px' }}
              />
            )}

            {/* Pagination failed — pause auto-loading and let the user retry. */}
            {myBookingsLoadMoreError && !isMyBookingsLoading && (
              <Flexbox column alignCenter gap={12} className='mt20' style={{ width: '100%' }}>
                <Text font14 inter regular grey2>Couldn't load more bookings.</Text>
                <Button
                  primaryOutlined
                  buttonMd
                  name='Retry'
                  onClick={loadMoreMyBookings}
                  isCalling={isMyBookingsLoading}
                />
              </Flexbox>
            )}
          </Flexbox>
        </>
      }
      </div>
    </>
  )
}

export default DashboardUser
